import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    const previousQuantity =
      parseInt(quantityInput?.dataset.lastQuantity || quantityInput?.defaultValue || quantityInput?.value || '1') ||
      1;

    // Add additional items as separate lines instead of increasing quantity
    if (quantity > previousQuantity) {
      const additionalItems = quantity - previousQuantity;
      this.#restoreQuantityInput(quantitySelector, previousQuantity);
      this.#addSeparateLineItems(line, additionalItems);
      return;
    }

    // Any decrease removes the line instead of decrementing the quantity
    if (quantity < previousQuantity) {
      this.#restoreQuantityInput(quantitySelector, previousQuantity);
      return this.onLineItemRemove(line);
    }

    // No change needed if quantity matches the current value
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Restore input value and button states after custom handling
   * @param {Element | null | undefined} quantitySelector - The quantity selector element
   * @param {number} quantity - Quantity to restore
   */
  #restoreQuantityInput(quantitySelector, quantity) {
    if (!quantitySelector) return;

    const quantityInput = quantitySelector.querySelector('input');
    if (quantityInput) {
      quantityInput.value = quantity.toString();
      quantityInput.dataset.lastQuantity = quantity.toString();
    }

    if ('updateButtonStates' in quantitySelector && typeof quantitySelector.updateButtonStates === 'function') {
      quantitySelector.updateButtonStates();
    }
  }

  /**
   * Adds new line items instead of increasing quantity on the cart page
   * @param {number} line - Current cart line (1-based)
   * @param {number} additionalItems - Number of extra items to add
   */
  async #addSeparateLineItems(line, additionalItems) {
    if (additionalItems <= 0) return;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('add:user-action');

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const targetItem = cart.items?.[line - 1];

      if (!targetItem) throw new Error('Cart line not found');

      const variantId = targetItem.variant_id?.toString();
      if (!variantId) throw new Error('Variant id missing');

      const sellingPlanId = targetItem.selling_plan_allocation?.selling_plan?.id;
      const baseProperties = { ...(targetItem.properties || {}) };

      // Remove internal unique properties if present; we'll create fresh ones per added item
      delete baseProperties._unique_id;
      delete baseProperties._line_item_id;
      delete baseProperties._item_index;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      this.#disableCartItems();
      this.refs.cartTotal?.shimmer?.();

      const sessionId = `cart_line_${variantId}_${Date.now()}_${performance.now()}`;
      /** @type {Record<string, string>} */
      let allSections = {};
      let hasError = false;
      let errorMessage = '';

      for (let i = 0; i < additionalItems; i++) {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', Array.from(sectionsToUpdate).join(','));
        formData.append('sections_url', window.location.pathname);

        if (sellingPlanId) {
          formData.append('selling_plan', sellingPlanId);
        }

        for (const [key, value] of Object.entries(baseProperties)) {
          if (value === null || value === undefined || value === '') continue;
          formData.append(`properties[${key}]`, value);
        }

        const uniqueId = `${sessionId}_${i}_${Date.now()}_${(performance.now() * 1000).toFixed(0)}_${Math.random()
          .toString(36)
          .slice(2, 9)}`;
        formData.append('properties[_unique_id]', uniqueId);
        formData.append('properties[_line_item_id]', `${uniqueId}_line`);
        formData.append('properties[_item_index]', `${i}`);

        const fetchCfg = fetchConfig('javascript', { body: formData });

        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: 'text/html',
          },
        });

        const responseData = await response.json();

        if (responseData.status) {
          hasError = true;
          errorMessage = responseData.message || responseData.description || 'Failed to add item to cart';
          break;
        }

        if (responseData.sections) {
          Object.assign(allSections, responseData.sections);
        }

        // Small delay helps Shopify treat additions as separate line items
        if (i < additionalItems - 1) {
          await new Promise((resolve) => setTimeout(resolve, 250));
        }
      }

      resetShimmer(this);

      if (hasError) {
        console.error(errorMessage);
        return;
      }

      const sectionHtml = allSections[this.sectionId];

      if (sectionHtml) {
        morphSection(this.sectionId, sectionHtml);
        this.#updateCartQuantitySelectorButtonStates();
      } else {
        sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      // Refresh cart count for header bubble
      let itemCount = 0;
      try {
        const latestCart = await fetch('/cart.js').then((res) => res.json());
        itemCount = latestCart?.item_count ?? 0;
      } catch (err) {
        console.error('Failed to fetch updated cart count', err);
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.sectionId, {
          source: 'cart-items-component',
          itemCount,
          sections: allSections,
        })
      );
    } catch (error) {
      console.error('Error adding separate cart items:', error);
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
